import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import { DatePipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { RE2JS } from 're2js';
import { UtilityService } from './utility.service';
import { Constants } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class GeneratePdfService {

  private readonly FONT_STYLES = {
    HEADING: { size: 12, type: 'bold' },
    SUBHEADING: { size: 10, type: 'bold' },
    QUESTION: { size: 9, type: 'normal' },
    RESPONSE: { size: 9, type: 'normal' },
    SIGNATURE_DETAILS: { size: 8, type: 'normal' }
  };

  private readonly COLORS = {
    PRIMARY: [15, 107, 193], // Blue
    BLACK: [0, 0, 0]
  };

  consentSignPopUpHeaderHtmlContent: string = environment.consentSignPopUpHeaderHtmlContent;
  consentSignPopUpHeaderHtmlContentForRS: string = environment.consentSignPopUpHeaderHtmlContentForRS;
  consentNonSignPopUpHeaderHtmlContent: string = environment.consentNonSignPopUpHeaderHtmlContent;
  consentNonSignPopUpHeaderHtmlContentForRS: string = environment.consentNonSignPopUpHeaderHtmlContentForRS;

  private readonly constant: Constants = new Constants();


  constructor(private datePipe: DatePipe, private utilityService: UtilityService) { }

  private parseColorValue(color: string): number[] {
    // Handle different color formats
    if (color.startsWith('#')) {
      // Handle hex colors
      const hex = color.substring(1);
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return [r, g, b];
    } else if (color.startsWith('rgb')) {
      // Handle rgb/rgba colors
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return [parseInt(match[0]), parseInt(match[1]), parseInt(match[2])];
      }
    } else {
      // Handle named colors
      const namedColors: { [key: string]: number[] } = {
        'black': [0, 0, 0],
        'red': [255, 0, 0],
        'green': [0, 128, 0],
        'blue': [0, 0, 255],
        'gray': [128, 128, 128],
        'purple': [128, 0, 128],
      };
      if (color.toLowerCase() in namedColors) {
        return namedColors[color.toLowerCase()];
      }
    }
    return this.COLORS.BLACK; // Default to black if color can't be parsed
  }

  generateAttestationPdf(user: any, sortedNonSignedQuestions: any[], sortedSignedQuestions: any[], surveyName: string, agencyName: string, agencyId: string, downloadLocally:boolean = true, providerType?: string): string {
    const doc = new jsPDF('p', 'mm', 'a4');
    this.addContentToDocument(doc, user, sortedNonSignedQuestions, sortedSignedQuestions, surveyName, agencyName, agencyId, providerType);

    // Get user's local browser timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timestamp = this.utilityService.getFilenameTimestamp('en-US', userTimeZone);
    let fileName = `${surveyName}_${agencyId}_Attestations`;
    fileName = fileName.toUpperCase().replace(/[ ]/gi, "_") + ".pdf";
    if(downloadLocally){
      doc.save(fileName);
      return;
    }
    else{
        let dataUri = doc.output('datauristring');
        // Remove the filename parameter if jsPDF inserted it
        dataUri = dataUri.replace(/data:application\/pdf;filename=[^;]+;base64,/, 'data:application/pdf;base64,');
            
        // Ensure we have the correct prefix format
        if (!dataUri.startsWith('data:application/pdf;base64,')) {
          const base64Data = dataUri.replace(/^data:application\/pdf[^,]*,/, '');
          dataUri = 'data:application/pdf;base64,' + base64Data;
        }
        const attestationFile = JSON.stringify([{ Id: 1, Name: fileName, value: dataUri }]);
        return attestationFile;
    }
  }

  private processQuestionAndResponse(doc: jsPDF, question: any, user: any, questionX: number, responseStartX: number, questionWidth: number, responseWidth: number, lineSpacing: number, yPos: number, margin: number): { yPos: number, totalHeightNeeded: number } {
    const userResponse = user.responses.find(r => r.questionId === question.questionId);
    const rawText = question.consentQuestionLabel || question.questionRichEditorText || '';
    const questionSegments = this.processHtmlToSegments(rawText);

    const { responseText, imageData, isSignature } = this.processResponseData(question, userResponse);
    const responseLines = doc.splitTextToSize(responseText, responseWidth);

    const questionHeight = this.calculateStyledTextHeight(doc, questionSegments, questionWidth, lineSpacing);
    const responseContentHeight = this.calculateResponseHeight(doc, question, userResponse, responseLines, isSignature, imageData, lineSpacing);
    const sectionHeight = Math.max(questionHeight, responseContentHeight);
    const paddingBetweenQuestions = 3;
    const totalHeightNeeded = sectionHeight + paddingBetweenQuestions;

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 10;
    const safePageHeight = pageHeight - pageMargin;
    if (yPos + totalHeightNeeded > safePageHeight && yPos > margin) {
        doc.addPage();
        yPos = margin;
    }

    const rowStartY = yPos;
    this.drawStyledText(doc, questionSegments, questionX, rowStartY, questionWidth, lineSpacing);
    this.drawResponse(doc, question, userResponse, responseStartX, rowStartY, responseWidth, lineSpacing, responseLines, isSignature, imageData);
    
    return { yPos, totalHeightNeeded };
  }

  private addContentToDocument(doc: jsPDF, user: any, sortedNonSignedQuestions: any[], sortedSignedQuestions: any[], surveyName: string, agencyName: string, agencyId: string, providerType?: string): void {
    const margin = 10;
    const gap = 18;
    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 2 * margin;
    const questionWidth = usableWidth * 0.70;
    const responseWidth = usableWidth * 0.30;
    const questionX = margin;
    const responseStartX = gap + questionWidth;
    let yPos = margin;

    // Add headers
    this.setHeaderStyles(doc, 'HEADING');
    doc.setTextColor(this.COLORS.PRIMARY[0], this.COLORS.PRIMARY[1], this.COLORS.PRIMARY[2]);
    
    if (providerType === 'RS') {
      // Two-line heading for RS provider type
      doc.text(this.constant.rsPdfHeadingLine1, pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      doc.text(this.constant.rsPdfHeadingLine2, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    } else {
      doc.text(surveyName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
    }

    this.setHeaderStyles(doc, 'SUBHEADING');
    this.drawHeaderField(doc, margin, yPos, 'Agency Id: ', `${user.agencyId}`);
    yPos += 7;
    this.drawHeaderField(doc, margin, yPos, 'Agency Name: ', `${user.agencyName}`);
    yPos += 10;
    
    doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);

    // Add questions and responses
    const lineSpacing = 5;

    // Render non-sign consent header (if present) before questions
    yPos = this.addConsentNonSignHeaderToDocument(doc, yPos, margin, usableWidth, questionX, lineSpacing, agencyName, agencyId, providerType);
   
    // Process non-signed questions
    for (const question of sortedNonSignedQuestions) {
      const result = this.processQuestionAndResponse(doc, question, user, questionX, responseStartX, questionWidth, responseWidth, lineSpacing, yPos, margin);
      yPos = result.yPos + result.totalHeightNeeded;
    }

    // Add consent sign header
    yPos = this.addConsentSignHeaderToDocument(doc, yPos, margin, usableWidth, questionX, lineSpacing, agencyName, agencyId, providerType);

    // Process signed questions
    for (const question of sortedSignedQuestions) {
      const result = this.processQuestionAndResponse(doc, question, user, questionX, responseStartX, questionWidth, responseWidth, lineSpacing, yPos, margin);
      yPos = result.yPos + result.totalHeightNeeded;
    }
  }

  addConsentNonSignHeaderToDocument(doc: jsPDF, yPos: number, margin: number, usableWidth: number, questionX: number, lineSpacing: number, agencyName: string, agencyId: string, providerType?: string): number {
    // Determine which content to use based on provider type
    let contentToUse = this.consentNonSignPopUpHeaderHtmlContent;
    if (providerType === 'RS') {
      contentToUse = this.consentNonSignPopUpHeaderHtmlContentForRS;
    }
    
    if (!contentToUse) {
      return yPos;
    }
    try {
        // Update agencyName and agencyId
        let headerContent = contentToUse.replace('<<Provider Legal Name>> <<MPI Number>>', `${agencyName}  ${agencyId}`);
        
        // Process the content with styles, using the default question size if no font-size is specified
        const headerSegments = this.processHtmlToSegments(headerContent, this.FONT_STYLES.QUESTION.size);
        //debugger;
        // Calculate required height
        const headerHeight = this.calculateStyledTextHeight(doc, headerSegments, usableWidth, lineSpacing);
        
        // Check for page break
        const pageHeight = doc.internal.pageSize.getHeight();
        const safePageHeight = pageHeight - margin;
        if (yPos + headerHeight > safePageHeight && yPos > margin) {
            doc.addPage();
            yPos = margin;
        }

        // Set initial styles - use standard question size for consistency
        doc.setFontSize(this.FONT_STYLES.QUESTION.size);
        doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);

        // Draw the content with proper styling using standard line spacing
        this.drawStyledText(doc, headerSegments, questionX, yPos, usableWidth, lineSpacing);
        
        // Add spacing after the consent header
        yPos += headerHeight + 10;
    } catch (e) {
      console.error('Error rendering consentNonSignPopUpHeaderHtmlContent:', e);
    }
    return yPos;
  }

  addConsentSignHeaderToDocument(doc: jsPDF, yPos: number, margin: number, usableWidth: number, questionX: number, lineSpacing: number, agencyName: string, agencyId: string, providerType?: string): number {
    // Determine which content to use based on provider type
    let contentToUse = this.consentSignPopUpHeaderHtmlContent;
    if (providerType === 'RS') {
      contentToUse = this.consentSignPopUpHeaderHtmlContentForRS;
    }
    
    if (!contentToUse) {
      return yPos;
    }
    try {
        // Update agencyName and agencyId
        let headerContent = contentToUse.replace('<<Provider Legal Name>> <<MPI Number>>', `${agencyName}  ${agencyId}`);
        
        // Process the content with styles, using the default question size if no font-size is specified
        const headerSegments = this.processHtmlToSegments(headerContent, this.FONT_STYLES.QUESTION.size);
        //debugger;
        // Calculate required height
        const headerHeight = this.calculateStyledTextHeight(doc, headerSegments, usableWidth, lineSpacing);
        
        // Check for page break
        const pageHeight = doc.internal.pageSize.getHeight();
        const safePageHeight = pageHeight - margin;
        if (yPos + headerHeight > safePageHeight && yPos > margin) {
            doc.addPage();
            yPos = margin;
        }

        // Set initial styles - use standard question size for consistency
        doc.setFontSize(this.FONT_STYLES.QUESTION.size);
        doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);

        // Draw the content with proper styling using standard line spacing
        this.drawStyledText(doc, headerSegments, questionX, yPos, usableWidth, lineSpacing);
        
        // Add spacing after the consent header
        yPos += headerHeight + 10;
    } catch (e) {
      console.error('Error rendering consentNonSignPopUpHeaderHtmlContent:', e);
    }
    return yPos;
  }


  drawHeaderField(doc: jsPDF, x: number, y: number, label: string, value: string): void {
    doc.setTextColor(this.COLORS.PRIMARY[0], this.COLORS.PRIMARY[1], this.COLORS.PRIMARY[2]);
    doc.text(label, x, y);    
    const labelWidth = doc.getTextWidth(label);    
    doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);
    doc.text(value, x + labelWidth, y);
  }

  setHeaderStyles(doc: jsPDF, style: 'HEADING' | 'SUBHEADING') {
    const fontStyle = this.FONT_STYLES[style];
    doc.setFontSize(fontStyle.size);
    doc.setFont("helvetica", fontStyle.type);
  }

  processStyleAttributes(node: HTMLElement): Set<string> {
    const styles = new Set<string>();
    
    // Get inline style attribute
    const styleAttr = node.getAttribute('style');
    if (styleAttr) {
      // Parse CSS style properties
      const styleProperties = styleAttr.split(';')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // Define style processors for dynamic handling
      const styleProcessors = {
        'font-weight': (value: string) => {
          if (value === 'bold' || parseInt(value, 10) >= 600) {
            return 'bold';
          }
          return null;
        },
        'font-style': (value: string) => {
          if (value === 'italic') {
            return 'italic';
          }
          return null;
        },
        'text-decoration': (value: string) => {
          if (value.includes('underline')) {
            return 'underline';
          }
          return null;
        },
        'color': (value: string) => `color:${value}`,
        'font-family': (value: string) => `font:${value}`,
        'font-size': (value: string) => {
          // Convert various font size units to a reasonable PDF font size
          let size: number;
          if (value.endsWith('px')) {
            size = parseInt(value) * 0.75; // Convert px to approximate PDF size
          } else if (value.endsWith('pt')) {
            size = parseInt(value); // PDF sizes are in points
          } else if (value.endsWith('em') || value.endsWith('rem')) {
            size = parseFloat(value) * 12; // Base size * em/rem value
          } else {
            size = parseInt(value); // For raw numbers or other units
          }
          
          // Ensure size is within reasonable bounds (8-16)
          if (!isNaN(size)) {
            size = Math.min(Math.max(8, size), 16);
            return `size:${size}`;
          }
          return null;
        }
      };

      // Process each style property dynamically
      for (const prop of styleProperties) {
        const [key, value] = prop.split(':').map(s => s.trim());
        if (!key || !value) continue;

        const normalizedKey = key.toLowerCase();
        
        // Check if we have a processor for this style
        if (styleProcessors[normalizedKey]) {
          const result = styleProcessors[normalizedKey](value);
          if (result) {
            styles.add(result);
          }
        } else {
          // For any other style, add it as-is with key:value format
          styles.add(`${normalizedKey}:${value}`);
        }
      }
    }

    return styles;
  }

  processHtmlToSegments(html: string, defaultFontSize?: number): any[] {
    if (!html) return [];

    // Clean and normalize input
    const sourceHtml = this.sanitizeHtmlInput(html, defaultFontSize);
    
    try {
      const div = document.createElement('div');
      div.innerHTML = sourceHtml;
      
      // Process lists with dynamic numbering
      this.processListElements(div);
      
      const segments: any[] = [];
      this.traverseHtmlNodes(div, new Set(), segments);
      
      return segments;
    } catch (e) {
      console.error("Error processing HTML, falling back to simple text:", e);
      return this.createFallbackSegments(html);
    }
  }

  private sanitizeHtmlInput(html: string, defaultFontSize?: number): string {
    return String(html)
      .replace(/^\uFEFF|\uFFFE|\uFFFF/g, '')
      .replace(/[\uFEFF\uFFFE\uFFFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .normalize('NFC')
      .replace(/^/, `<div style="font-size: ${defaultFontSize || this.FONT_STYLES.QUESTION.size}pt">`)
      .replace(/$/, '</div>');
  }

  private processListElements(container: HTMLElement): void {
    const getAlphabeticLabel = (num: number): string => {
      let label = '';
      while (num > 0) {
        num--;
        label = String.fromCharCode(97 + (num % 26)) + label;
        num = Math.floor(num / 26);
      }
      return label;
    };

    const processList = (listElement: HTMLElement, isOrdered: boolean, start = 1) => {
      let counter = start;
      Array.from(listElement.children).forEach(child => {
        if (child.tagName === 'LI') {
          const prefixText = isOrdered ? `${getAlphabeticLabel(counter)}. ` : '• ';
          const prefix = document.createElement('span');
          prefix.textContent = prefixText;
          prefix.setAttribute('data-list-prefix', 'true');
          child.insertBefore(prefix, child.firstChild);
          
          // Process nested lists
          child.querySelectorAll('ol, ul').forEach(nestedList => {
            const isNestedOrdered = nestedList.tagName === 'OL';
            const nestedStart = isNestedOrdered ? parseInt(nestedList.getAttribute('start') || '1', 10) : 1;
            processList(nestedList as HTMLElement, isNestedOrdered, nestedStart);
          });
          counter++;
        }
      });
    };

    // Process all top-level lists
    container.querySelectorAll('ol:not(li ol), ul:not(li ul)').forEach(list => {
      const isOrdered = list.tagName === 'OL';
      const start = isOrdered ? parseInt(list.getAttribute('start') || '1', 10) : 1;
      processList(list as HTMLElement, isOrdered, start);
    });
  }

  private traverseHtmlNodes(node: Node, parentStyles: Set<string>, segments: any[]): void {
    if (node.nodeType === Node.TEXT_NODE) {
      this.processTextNode(node, parentStyles, segments);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      this.processElementNode(node as HTMLElement, parentStyles, segments);
    }
  }

  private processTextNode(node: Node, parentStyles: Set<string>, segments: any[]): void {
    let text = node.textContent || '';
    
    // Clean text while preserving meaningful whitespace
    text = text
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[ \t\r\n]+/g, (match) => match.length > 1 ? ' ' : match);
    
    if (text.trim()) {
      segments.push({
        text: text,
        styles: parentStyles,
        isListPrefix: node.parentElement?.getAttribute('data-list-prefix') === 'true'
      });
    }
  }

  private processElementNode(element: HTMLElement, parentStyles: Set<string>, segments: any[]): void {
    const nodeName = element.nodeName.toLowerCase();
    const tagConfig = this.getTagConfiguration(nodeName);
    
    // Add newline for block elements
    if (tagConfig.isBlock && segments.length > 0 && !segments[segments.length - 1].isNewline) {
      segments.push({ isNewline: true });
    }

    // Apply spacing for specific elements
    if (tagConfig.addSpacing && segments.length > 0) {
      segments.push({ isNewline: true, isHeadingSpacing: true });
    }

    // Merge parent styles with element styles
    const newStyles = this.mergeElementStyles(element, parentStyles, tagConfig);

    // Handle special element behaviors
    this.handleSpecialElements(element, parentStyles, segments, tagConfig);

    // Process all child nodes
    Array.from(element.childNodes).forEach(child => {
      this.traverseHtmlNodes(child, newStyles, segments);
    });

    // Add closing newline for block elements
    if (tagConfig.isBlock && segments.length > 0 && !segments[segments.length - 1].isNewline) {
      segments.push({ isNewline: true });
    }
  }

  private getTagConfiguration(tagName: string): any {
    const tagConfigs = {
      // Block elements
      'p': { isBlock: true, resetFontSize: true },
      'div': { isBlock: true },
      'br': { isBlock: true },
      'li': { isBlock: true },
      
      // Headings with spacing
      'h1': { isBlock: true, addSpacing: true, styles: ['bold'] },
      'h2': { isBlock: true, addSpacing: true, styles: ['bold'] },
      'h3': { isBlock: true, addSpacing: true, styles: ['bold'] },
      'h4': { isBlock: true, addSpacing: true, styles: ['bold'] },
      'h5': { isBlock: true, addSpacing: true, styles: ['bold'] },
      'h6': { isBlock: true, addSpacing: true, styles: ['bold'] },
      
      // Inline formatting
      'strong': { styles: ['bold'] },
      'b': { styles: ['bold'] },
      'em': { styles: ['italic'] },
      'i': { styles: ['italic'] },
      'u': { styles: ['underline'] },
      
      // Special elements
      'a': { isSpecial: true, styles: ['underline'] },
      
      // Default configuration
      'default': { isBlock: false, styles: [] }
    };

    return tagConfigs[tagName] || tagConfigs['default'];
  }

  private mergeElementStyles(element: HTMLElement, parentStyles: Set<string>, tagConfig: any): Set<string> {
    let newStyles = new Set(parentStyles);

    // Apply tag-based styles
    if (tagConfig.styles) {
      tagConfig.styles.forEach(style => newStyles.add(style));
    }

    // Reset font size for paragraph tags
    if (tagConfig.resetFontSize) {
      newStyles = new Set(Array.from(newStyles).filter(style => !style.startsWith('size:')));
      newStyles.add(`size:${this.FONT_STYLES.QUESTION.size}`);
    }

    // Handle special anchor tag styling
    if (element.nodeName.toLowerCase() === 'a') {
      const styleAttr = element.getAttribute('style') || '';
      
      // Add default blue color if not overridden by inline styles
      if (!styleAttr.includes('color:')) {
        newStyles.add(`color:rgb(${this.COLORS.PRIMARY.join(',')})`);
      }
      
      // Add underline if not overridden by inline styles
      if (!styleAttr.includes('text-decoration:')) {
        newStyles.add('underline');
      }
    }

    // Process inline styles using the dynamic processStyleAttributes method
    const inlineStyles = this.processStyleAttributes(element);
    inlineStyles.forEach(style => newStyles.add(style));

    return newStyles;
  }

  private handleSpecialElements(element: HTMLElement, parentStyles: Set<string>, segments: any[], tagConfig: any): void {
    const nodeName = element.nodeName.toLowerCase();
    
    // Handle anchor tags - add spacing before anchor content
    if (nodeName === 'a') {
      segments.push({
        text: ' ',
        styles: parentStyles,
        isListPrefix: false
      });
    }
  }

  private createFallbackSegments(html: string): any[] {
    const cleanHtml = html
      .replace(/[\uFEFF\uFFFE\uFFFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\t\n\r]+/g, '')
      .replace(/ {2,}/g, ' ')
      .trim();
    
    const div = document.createElement('div');
    div.innerHTML = cleanHtml;
    
    return [{ text: div.innerText, styles: new Set(), isListPrefix: false }];
  }

  processResponseData(question: any, userResponse: any): { responseText: string, imageData: string | null, isSignature: boolean } {
    let responseText = 'No response';
    let imageData = null;
    let isSignature = false;

    if (userResponse) {
        if (question.inputType === 'sign' && userResponse.responseText) {
            isSignature = true;
            try {
                const signDet = JSON.parse(userResponse.responseText);
                imageData = signDet["value"];
                responseText = '';
            } catch (e) {
                console.error("Error parsing signature data", e);
                responseText = 'Error displaying signature';
            }
        } else if (question.inputType === 'fileupload' && userResponse.responseText) {
            try {
                const azureDet = JSON.parse(userResponse.responseText);
                responseText = azureDet["Files"].map(elem => elem.Name).join(", ");
            } catch (e) {
                console.error("Error parsing fileupload data", e);
                responseText = 'Error parsing file data';
            }
        } else {
            responseText = (userResponse.responses && userResponse.responses.length > 0)
                ? userResponse.responses.join(', ')
                : userResponse.responseText || 'No response';
        }
    }
    return { responseText, imageData, isSignature };
  }

  calculateStyledTextHeight(doc: jsPDF, segments: any[], maxWidth: number, lineSpacing: number): number {
      let tempY = 0;
      let tempX = 0;
      const spaceWidth = doc.getTextWidth(' ');
      doc.setFontSize(this.FONT_STYLES.QUESTION.size);
      let hasContent = false;

      segments.forEach(segment => {
          if (segment.isNewline) {
              if (tempX > 0 || hasContent) {
                  // Add extra spacing for heading spacing
                  const spacing = lineSpacing;
                  tempY += spacing;
                  hasContent = false;
              }
              tempX = 0;
              return;
          }

          // Apply font styles
          let fontStyle = 'normal';
          if (segment.styles.has('bold')) fontStyle = 'bold';
          if (segment.styles.has('italic')) fontStyle = 'italic';
          if (segment.styles.has('bold') && segment.styles.has('italic')) fontStyle = 'bolditalic';
          doc.setFont('helvetica', fontStyle);

          // Handle custom font size
          const sizeStyle = Array.from(segment.styles).find((s): s is string => typeof s === 'string' && s.startsWith('size:'));
          if (sizeStyle) {
              const size = parseInt(sizeStyle.split(':')[1]);
              if (!isNaN(size)) {
                  doc.setFontSize(size);
              }
          }

          const words = segment.text.split(' ');
          words.forEach(word => {
              if (!word) return;
              const wordWidth = doc.getTextWidth(word);
              
              // Check if word needs to wrap
              if (tempX > 0 && tempX + wordWidth > maxWidth) {
                  tempY += lineSpacing;
                  tempX = 0;
              }
              
              tempX += wordWidth + spaceWidth;
              hasContent = true;
          });
      });

      // Ensure minimum height and add final line spacing
      return Math.max(tempY + (hasContent ? lineSpacing : 0), lineSpacing);
  }

  calculateResponseHeight(doc: jsPDF, question: any, userResponse: any, responseLines: string[], isSignature: boolean, imageData: string | null, lineSpacing: number): number {
    const minHeight = lineSpacing; // Minimum height for any response
    
    // Handle signature responses
    if (isSignature && imageData) {
        const signatureHeight = 30; // Base height for signature image
        const signatureDetailsHeight = 20; // Additional height for signature details
        return Math.max(signatureHeight + signatureDetailsHeight, minHeight);
    }
    
    // Handle choice-based responses (radio buttons, checkboxes, dropdowns)
    const choiceDataTypes = ['radiobuttons', 'rdo', 'checkbox', 'chk', 'dropdown', 'cmb'];
    if (userResponse && choiceDataTypes.includes(question.dataType) && question.options && question.options.length > 0) {
        const responseValue = (userResponse.responses && userResponse.responses.length > 0)
            ? userResponse.responses
            : (userResponse.responseText ? [userResponse.responseText] : []);
            
        const selectedOptions = question.options.filter(option => responseValue.includes(option.value));
        if (selectedOptions.length > 0) {
            // Add some padding between options and calculate total height
            const optionSpacing = lineSpacing * 1.2; // Slightly more space between options
            const optionsHeight = selectedOptions.length * optionSpacing;
            return Math.max(optionsHeight, minHeight);
        }
    }
    
    // Handle text responses
    if (responseLines && responseLines.length > 0) {
        const textHeight = responseLines.length * lineSpacing;
        // Add padding for better readability
        const padding = lineSpacing * 0.5;
        return Math.max(textHeight + padding, minHeight);
    }
    
    // Return minimum height if no content
    return minHeight;
  }

  drawStyledText(doc: jsPDF, segments: any[], x: number, y: number, maxWidth: number, lineSpacing: number) {
      let currentX = x;
      let currentY = y;
      doc.setFontSize(this.FONT_STYLES.QUESTION.size);
      doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);

      let lineIndent = 0;
      let currentLine = '';
      let currentLineWidth = 0;

      const renderLine = (text: string, atX: number, atY: number, style: string, styles: Set<string>) => {
          // Set font style
          let fontFamily = 'helvetica';
          let fontSize = this.FONT_STYLES.QUESTION.size;

          // Handle custom font family
          const fontStyle = Array.from(styles).find(s => s.startsWith('font:'));
          if (fontStyle) {
              const font = fontStyle.split(':')[1];
              // Only use supported fonts
              if (['helvetica', 'courier', 'times'].includes(font.toLowerCase())) {
                  fontFamily = font.toLowerCase();
              }
          }

          // Handle custom font size
          const sizeStyle = Array.from(styles).find(s => s.startsWith('size:'));
          if (sizeStyle) {
              const size = parseInt(sizeStyle.split(':')[1]);
              if (!isNaN(size)) {
                  fontSize = size;
              }
          }

          doc.setFont(fontFamily, style);
          doc.setFontSize(fontSize);

          // Handle custom colors
          const colorStyle = Array.from(styles).find(s => s.startsWith('color:'));
          if (colorStyle) {
              const color = colorStyle.split(':')[1];
              const [r, g, b] = this.parseColorValue(color);
              doc.setTextColor(r, g, b);
          } else {
              doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);
          }

          // Only remove BOM markers, keep all other characters including special characters
          const cleanedText = text.replace(/[\uFEFF\uFFFE\uFFFF]/g, '');
          
          // Render the text
          doc.text(cleanedText, atX, atY);

          // Handle underline
          if (styles.has('underline')) {
              const textWidth = doc.getTextWidth(cleanedText);
              doc.line(atX, atY + 1, atX + textWidth, atY + 1);
          }
      };

      segments.forEach(segment => {
          if (segment.isNewline) {
              if (currentLine) {
                  renderLine(currentLine, x + lineIndent, currentY, 'normal', new Set());
                  currentLine = '';
                  currentLineWidth = 0;
              }
              currentY += lineSpacing;
              currentX = x;
              return;
          }

          let fontStyle = 'normal';
          if (segment.styles.has('bold')) fontStyle = 'bold';
          if (segment.styles.has('italic')) fontStyle = 'italic';
          if (segment.styles.has('bold') && segment.styles.has('italic')) fontStyle = 'bolditalic';

          if (segment.isListPrefix) {
              lineIndent = doc.getTextWidth(segment.text);
              renderLine(segment.text, currentX, currentY, fontStyle, segment.styles);
              currentX = x + lineIndent;
              return;
          }

          let text = segment.text;
          const availableWidth = maxWidth - lineIndent;

          while (text) {
              const textWidth = doc.getTextWidth(text);
              if (currentX + textWidth <= x + maxWidth) {
                  renderLine(text, currentX, currentY, fontStyle, segment.styles);
                  currentX += textWidth;
                  text = '';
              } else {
                  // Find the longest substring that fits
                  let i = text.length;
                  while (i > 0 && doc.getTextWidth(text.substring(0, i)) > availableWidth) {
                      i--;
                  }
                  
                  // Find word boundary
                  while (i > 0 && !/\s/.test(text[i]) && text[i] !== '-') {
                      i--;
                  }
                  
                  if (i === 0) {
                      // If no word boundary found, force split
                      i = Math.floor(text.length / 2);
                  }

                  const lineText = text.substring(0, i).trim();
                  if (lineText) {
                      renderLine(lineText, currentX, currentY, fontStyle, segment.styles);
                  }
                  
                  text = text.substring(i).trim();
                  if (text) {
                      currentY += lineSpacing;
                      currentX = x + lineIndent;
                  }
              }
          }
      });
  }

  drawResponse(doc: jsPDF, question: any, userResponse: any, x: number, y: number, width: number, lineSpacing: number, responseLines: string[], isSignature: boolean, imageData: string | null) {
    const responseY = y;
    if (isSignature && imageData) {
        doc.addImage(imageData, 'PNG', x, responseY, 50, 25);
        const signDet = JSON.parse(userResponse.responseText);
        const otherData = signDet["key"].split("||");
        let detailsYPos = responseY + 25 + 5;
        doc.setFontSize(this.FONT_STYLES.SIGNATURE_DETAILS.size);
        doc.text(`Name: ${otherData[0]}`, x, detailsYPos);
        detailsYPos += 4;
        doc.text(`Role: ${otherData[1]}`, x, detailsYPos);
        detailsYPos += 4;
        // doc.text(`Email: ${otherData[2]}`, x, detailsYPos);
        // detailsYPos += 4;
        doc.text(`Signed On: ${new Date(otherData[3]).toLocaleDateString()}`, x, detailsYPos);
        doc.setFontSize(this.FONT_STYLES.RESPONSE.size);
    } else {
        const choiceDataTypes = ['radiobuttons', 'rdo', 'checkbox', 'chk', 'dropdown', 'cmb'];
        if (userResponse && choiceDataTypes.includes(question.dataType) && question.options?.length > 0) {
            const responseValue = (userResponse.responses && userResponse.responses.length > 0)
                ? userResponse.responses
                : (userResponse.responseText ? [userResponse.responseText] : []);
            
            const selectedOptions = question.options.filter(option => responseValue.includes(option.value));
            let checkY = responseY;
            selectedOptions.forEach((option) => {
                const boxSize = 4;
                const boxX = x;
                const textX = x + boxSize + 2;

                doc.setDrawColor(this.COLORS.PRIMARY[0], this.COLORS.PRIMARY[1], this.COLORS.PRIMARY[2]);
                if (question.dataType === 'radiobuttons' || question.dataType === 'rdo') {
                    doc.setLineWidth(0.3);
                    doc.circle(boxX + (boxSize / 2), checkY, boxSize / 2, 'S');
                    if (responseValue.includes(option.value)) {
                        doc.setFillColor(this.COLORS.PRIMARY[0], this.COLORS.PRIMARY[1], this.COLORS.PRIMARY[2]);
                        doc.circle(boxX + (boxSize / 2), checkY, boxSize / 3, 'F');
                    }
                } else {
                    doc.rect(boxX, checkY - (boxSize / 2), boxSize, boxSize, 'S');
                    if (question.dataType === 'dropdown' || question.dataType === 'cmb') {
                        doc.setLineWidth(0.5);
                        const arrowX = boxX + boxSize / 2;
                        const arrowY = checkY - boxSize / 4;
                        doc.line(arrowX - 1.5, arrowY, arrowX, arrowY + 1.5);
                        doc.line(arrowX, arrowY + 1.5, arrowX + 1.5, arrowY);
                    }
                    else if (responseValue.includes(option.value)) {
                        doc.setTextColor(this.COLORS.PRIMARY[0], this.COLORS.PRIMARY[1], this.COLORS.PRIMARY[2]);
                        doc.setFont('ZapfDingbats');
                        doc.setFontSize(8);
                        doc.text('4', boxX + boxSize / 2, checkY, { align: 'center', baseline: 'middle' });
                        doc.setFontSize(this.FONT_STYLES.RESPONSE.size);
                    }
                }
                doc.setTextColor(this.COLORS.BLACK[0], this.COLORS.BLACK[1], this.COLORS.BLACK[2]);
                doc.setFont('helvetica', 'normal');
                doc.text(option.value, textX, checkY);
                checkY += lineSpacing;
            });
        } else {
            doc.setFont('helvetica', 'normal');
            doc.text(responseLines, x, responseY);
        }
    }
  }

}